import anime from 'animejs';
import has from 'lodash/has';
import isBoolean from 'lodash/isBoolean';
import isEmpty from 'lodash/isEmpty';
import isObject from 'lodash/isObject';
import { action, computed, observable, reaction } from 'mobx';
import PubSub from 'pubsub-js';

import {
  CHAT_WINDOW_MODE_CLOSED,
  CHAT_WINDOW_MODE_DEFAULT,
  CHAT_WINDOW_MODE_EXTENDED,
  CHAT_WINDOW_MODE_FULLSCREEN,
  CHAT_WINDOW_MODE_HALFSCREEN,
  CHAT_WINDOW_MODE_MINIMIZED,
  CHAT_WINDOW_VARIANT_EMBEDDED,
  CHAT_WINDOW_VARIANT_FULLSCREEN,
  CHAT_WINDOW_VARIANT_POP_IN,
  getCookie,
  PUBSUB_CHAT_WINDOW_CLOSE,
  PUBSUB_CHAT_WINDOW_CLOSED,
  PUBSUB_CHAT_WINDOW_MINIMIZED,
  PUBSUB_CHAT_WINDOW_OPEN,
  PUBSUB_CHAT_WINDOW_OPENED
} from '../utilities';

export default class ChatWindow {
  constructor(rootStore) {
    this.rootStore = rootStore;
    this.variant = this.rootStore.config.window.variant;
    this.setInitialDimensions();
    this.addWindowResizeReaction();

    if (getCookie('redirectURL_cookie')) {
      this.setCookie();
      setTimeout(() => this.open(), this.animationTimeout);
    }

    if (this.rootStore.config.window.openOnInit) {
      setTimeout(() => this.open(), this.animationTimeout);
    }

    const queryParameters = new URLSearchParams(window.location.search);
    const getQues = queryParameters.get('Ques');
    if (getQues) {
      setTimeout(() => {
        this.open();

        const interval = setInterval(() => {
          if (document.querySelector('#messages-content')) {
            const element = document.querySelector('#messages-content').getElementsByTagName('h3');

            if (element.length) {
              clearInterval(interval);
              setTimeout(() => {
                this.rootStore.chat.isMessaging = true;
                return this.rootStore.bot.askQuestion({
                  Question: getQues,
                  TextLanguage: 'en',
                  hideUserMessage: false
                });
              }, 1000);
            }
          }
        }, 100);
      }, 1000);
    }

    PubSub.subscribe(PUBSUB_CHAT_WINDOW_OPEN, () => {
      this.open();
    });

    PubSub.subscribe(PUBSUB_CHAT_WINDOW_CLOSE, () => {
      this.close();
    });
  }

  @observable isCookie = false;

  @observable _mode = CHAT_WINDOW_MODE_CLOSED;

  @computed get mode() {
    return this._mode;
  }

  set mode(value) {
    this.prevMode = this.mode;
    this._mode = value;
  }

  @observable variant = CHAT_WINDOW_VARIANT_POP_IN;

  @observable isAnimating = false;

  get hasAnimation() {
    return (
      has(this.rootStore.config.window, 'animation') &&
      !isEmpty(this.rootStore.config.window.animation)
    );
  }

  @computed get animationTimeout() {
    if (!this.hasAnimation) {
      return 0;
    }

    return this.rootStore.config.window.animation.timeout || 300;
  }

  @observable dimensions = {
    width: 0,
    height: 0,
    minWidth: 0,
    minHeight: 0,
    offsetX: 0,
    offsetY: 0,
    x: 0,
    y: 0
  };

  @action open() {
    if (
      this.rootStore.ui.isMobile ||
      this.rootStore.config.window.variant === CHAT_WINDOW_VARIANT_FULLSCREEN ||
      (typeof this.rootStore.config.window.setMaxWidth !== 'undefined' &&
        typeof this.rootStore.config.window.setMaxHeight !== 'undefined' &&
        this.rootStore.config.window.setMaxWidth &&
        this.rootStore.config.window.setMaxHeight)
    ) {
      this.setFullScreen();
    } else {
      this.setDefault();
    }

    PubSub.publish(PUBSUB_CHAT_WINDOW_OPENED);
  }

  @action close() {
    this.mode = CHAT_WINDOW_MODE_CLOSED;
    PubSub.publish(PUBSUB_CHAT_WINDOW_CLOSED);
  }

  @action minimize() {
    this.mode = CHAT_WINDOW_MODE_MINIMIZED;
    PubSub.publish(PUBSUB_CHAT_WINDOW_MINIMIZED);
  }

  @action setCookie() {
    this.isCookie = true;
  }

  @action toggle() {
    if (this.isMinimized || this.isClosed) {
      this.open();
    } else {
      this.close();
    }
  }

  @action setMode(mode) {
    this.mode = mode;
  }

  @action setVariant(variant) {
    this.variant = variant;
  }

  @action setAnimating(isAnimating) {
    this.isAnimating = isAnimating;
  }

  @action setInitialDimensions() {
    const maxWidth = window.innerWidth + this.rootStore.config.window.offset.horizontal;
    const maxHeight = window.innerHeight + this.rootStore.config.window.offset.vertical;

    this.dimensions.width =
      this.rootStore.config.window.width > maxWidth ? maxWidth : this.rootStore.config.window.width;
    this.dimensions.height =
      this.rootStore.config.window.height > maxHeight
        ? maxHeight
        : this.rootStore.config.window.height;
    this.dimensions.minWidth =
      this.rootStore.config.window.minWidth > maxWidth
        ? maxWidth
        : this.rootStore.config.window.minWidth;
    this.dimensions.minHeight =
      this.rootStore.config.window.minHeight > maxHeight
        ? maxHeight
        : this.rootStore.config.window.minHeight;
    this.dimensions.offsetX = this.rootStore.config.window.offset.horizontal;
    this.dimensions.offsetY = this.rootStore.config.window.offset.vertical;

    if (this.isPopIn) {
      this.dimensions.x =
        this.rootStore.ui.windowSize.width - this.dimensions.width + this.dimensions.offsetX;
      this.dimensions.y =
        this.rootStore.ui.windowSize.height - this.dimensions.height + this.dimensions.offsetY;
    }

    if (this.isEmbedded) {
      this.dimensions.x = 0;
      this.dimensions.y = 0;
    }

    if (
      typeof this.rootStore.config.window.setMaxWidth !== 'undefined' &&
      typeof this.rootStore.config.window.setMaxHeight !== 'undefined'
    ) {
      if (this.rootStore.config.window.setMaxWidth && !this.rootStore.config.window.setMaxHeight) {
        this.dimensions.width = this.rootStore.ui.windowSize.width;
        this.dimensions.x = 0;
        this.dimensions.y =
          this.rootStore.ui.windowSize.height - this.dimensions.height + this.dimensions.offsetY;
      }

      if (this.rootStore.config.window.setMaxHeight && !this.rootStore.config.window.setMaxWidth) {
        this.dimensions.height = this.rootStore.ui.windowSize.height;
        this.dimensions.x =
          this.rootStore.ui.windowSize.width - this.dimensions.width + this.dimensions.offsetX;
        this.dimensions.y = 0;
      }
    }
  }

  @action setExtended() {
    this.dimensions.height = this.rootStore.ui.windowSize.height;
    this.mode = CHAT_WINDOW_MODE_EXTENDED;
  }

  @action setFullScreen() {
    this.dimensions.x = 0;
    this.dimensions.y = 0;
    this.dimensions.height = this.rootStore.ui.windowSize.height;
    this.dimensions.width = this.rootStore.ui.windowSize.width;
    this.mode = CHAT_WINDOW_MODE_FULLSCREEN;
  }

  @action setHalfScreen() {
    if (!this.canSetHalfScreen) {
      console.warn('minWidth more than half screen width');
      return;
    }

    this.dimensions.y = 0;
    this.dimensions.x = this.rootStore.ui.windowSize.width / 2;
    this.dimensions.width = this.rootStore.ui.windowSize.width / 2;
    this.dimensions.height = this.rootStore.ui.windowSize.height;
    this.mode = CHAT_WINDOW_MODE_HALFSCREEN;
  }

  @action setDefault(reset = true) {
    this.mode = CHAT_WINDOW_MODE_DEFAULT;

    if (reset) {
      this.setInitialDimensions();
    }
  }

  @action setClosed() {
    this.mode = CHAT_WINDOW_MODE_CLOSED;
    this.setInitialDimensions();
  }

  @action onDragStop = (event, { x, y }) => {
    const eps = 0.5;
    const isTop = y <= eps;
    const isRightSide =
      x + this.dimensions.width === this.rootStore.ui.windowSize.width ||
      x + this.dimensions.width === this.rootStore.ui.windowSize.width - eps;

    this.dimensions.x = x;
    this.dimensions.y = y;

    const checkResizable = () => {
      const resizableConfig = this.rootStore.config.window.resizable;

      if (isObject(resizableConfig)) {
        const values = Object.values(resizableConfig);
        const result = values.filter(value => value === true).length;
        return result;
      }

      // if typeof resizableConfig === 'boolean'
      return resizableConfig;
    };

    const isResizable = checkResizable();

    if (
      (!this.isExtended && !this.isHalfScreen && isTop && isResizable) ||
      (this.isHalfScreen && !isRightSide)
    ) {
      this.setExtended();
      return;
    }

    if (!this.isHalfScreen && isRightSide && this.canSetHalfScreen && isResizable) {
      this.setHalfScreen();
    }
  };

  @action onResizeStop = (event, direction, { offsetWidth, offsetHeight }, delta, position) => {
    this.dimensions.width = offsetWidth;
    this.dimensions.height = offsetHeight;
    this.dimensions.y = position.y;
    this.dimensions.x = position.x;

    if (
      (this.isExtended || this.isHalfScreen) &&
      offsetHeight !== this.rootStore.ui.windowSize.height
    ) {
      this.setDefault(false);
    }
  };

  @action onEnter = node => {
    if (!this.hasAnimation) {
      return;
    }

    if (
      isObject(this.rootStore.config.window.animation.unminimize) &&
      this.prevMode === CHAT_WINDOW_MODE_MINIMIZED
    ) {
      this.isAnimating = true;

      anime({
        targets: node,
        ...this.rootStore.config.window.animation.unminimize
      });

      return;
    }

    if (isObject(this.rootStore.config.window.animation.open)) {
      this.isAnimating = true;

      anime({
        targets: node,
        ...this.rootStore.config.window.animation.open
      });
    }
  };

  @action onEntered = () => {
    this.isAnimating = false;
  };

  @action onExit = node => {
    if (!this.hasAnimation) {
      return;
    }

    if (isObject(this.rootStore.config.window.animation.minimize) && this.isMinimized) {
      this.isAnimating = true;
      anime({ targets: node, ...this.rootStore.config.window.animation.minimize });
      return;
    }

    if (isObject(this.rootStore.config.window.animation.close)) {
      this.isAnimating = true;
      anime({ targets: node, ...this.rootStore.config.window.animation.close });
    }
  };

  @action onExited = () => {
    this.isAnimating = false;
  };

  @action addWindowResizeReaction() {
    return reaction(
      () => this.rootStore.ui.windowSize,
      () => {
        if (this.isFullScreen) {
          this.setFullScreen();
          return;
        }

        if (this.isHalfScreen) {
          if (this.canSetHalfScreen) {
            this.setHalfScreen();
          } else {
            this.setFullScreen();
          }

          return;
        }

        if (this.isOpened && !this.isFullScreen) {
          this.setDefault();
        }
      }
    );
  }

  @computed.struct get resizingConfig() {
    const hasConfig =
      isObject(this.rootStore.config.window.resizable) ||
      isBoolean(this.rootStore.config.window.resizable);

    if (!hasConfig || this.isFullScreen || this.isEmbedded) {
      return {
        bottom: false,
        bottomLeft: false,
        bottomRight: false,
        left: false,
        right: false,
        top: false,
        topLeft: false,
        topRight: false
      };
    }

    if (hasConfig) {
      return this.rootStore.config.window.resizable;
    }

    return {
      bottom: true,
      bottomLeft: true,
      bottomRight: true,
      left: true,
      right: true,
      top: true,
      topLeft: true,
      topRight: true
    };
  }

  /**
   * @todo include offsets
   * (add top, left, bottom, right offset)
   */
  @computed get resizeHandleStyles() {
    const handleSize = 10;
    const offset = this.rootStore.ui.isVisibleExtraHeader
      ? this.rootStore.config.header.avatar.height -
        this.rootStore.config.header.height -
        handleSize / 2
      : 0;

    return {
      topRight: {
        top: offset
      },
      topLeft: {
        top: offset
      },
      top: {
        top: offset
      },
      left: {
        top: offset,
        height: `calc(100% - ${offset}px)`
      },
      right: {
        top: offset,
        height: `calc(100% - ${offset}px)`
      }
    };
  }

  @computed get canSetHalfScreen() {
    return this.rootStore.ui.windowSize.width / 2 >= this.dimensions.minWidth;
  }

  @computed get isDraggable() {
    return this.rootStore.config.window.draggable && !this.isFullScreen && !this.isEmbedded;
  }

  @computed get isVisible() {
    if (!this.hasAnimation) {
      return this.isOpened;
    }

    return this.isOpened || (!this.isOpened && this.isAnimating);
  }

  @computed get isDefault() {
    return this.mode === CHAT_WINDOW_MODE_DEFAULT;
  }

  @computed get isHalfScreen() {
    return this.mode === CHAT_WINDOW_MODE_HALFSCREEN;
  }

  @computed get isExtended() {
    return this.mode === CHAT_WINDOW_MODE_EXTENDED;
  }

  @computed get isFullScreen() {
    return this.mode === CHAT_WINDOW_MODE_FULLSCREEN;
  }

  @computed get isMinimized() {
    return this.mode === CHAT_WINDOW_MODE_MINIMIZED;
  }

  @computed get isClosed() {
    return this.mode === CHAT_WINDOW_MODE_CLOSED;
  }

  @computed get isOpened() {
    return !this.isClosed && !this.isMinimized;
  }

  @computed get isEmbedded() {
    return this.variant === CHAT_WINDOW_VARIANT_EMBEDDED;
  }

  @computed get isFullscreenVariant() {
    return this.variant === CHAT_WINDOW_VARIANT_FULLSCREEN;
  }

  @computed get isPopIn() {
    return this.variant === CHAT_WINDOW_VARIANT_POP_IN;
  }
}
